import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";

const styles = StyleSheet.create({
  body: {
    padding: 15,
    fontSize: 9, // bigger text
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 3,
    fontWeight: "bold",
  },
  subHeader: {
    fontSize: 10,
    textAlign: "center",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  box: {
    border: 1,
    borderColor: "#000",
    marginBottom: 6,
  },
  tableRow: {
    flexDirection: "row",
  },
  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 4, // bigger padding
    textAlign: "center",
    flexGrow: 1,
    fontSize: 9, // bigger text inside tables
    width: "15%",
  },

  cell_amt: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 4, // bigger padding
    textAlign: "center",
    flexGrow: 1,
    fontSize: 9, // bigger text inside tables
    width: "15%",
    fontFamily: "Roboto",
  },
  wideCell: {
    flexGrow: 2,
  },
  underline: {
    borderBottom: 1,
    borderColor: "#000",
    width: "250%",
    margin: "auto",
    marginBottom: 3,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 20,
  },
  signBox: {
    textAlign: "center",
  },
});

export default function DailyStocksReportPrint({ dsr, profile }) {
  // const formatNum = (val) => parseFloat(val || 0).toLocaleString();
  const formatNum = (val) =>
    parseFloat(val || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const denominationsData = Object.entries(dsr.denomination_list).map(
    ([denom, pcs]) => ({
      denom,
      pcs: parseFloat(pcs),
      amount: parseFloat(denom) * parseFloat(pcs),
    })
  );
  const totalCash = denominationsData.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  return (
    <Document>
      {/* Landscape Page */}
      <Page size="A4" orientation="landscape" style={styles.body}>
        {/* Title */}
        <Text style={styles.header}>C - 101 - FINAL SUMMARY REPORT</Text>
        <Text style={styles.subHeader}>KGAZ STATION</Text>

        {/* Cashier + Date */}
        <View style={styles.row}>
          <Text>CASHIER: {`${profile.firstName} ${profile.lastName}`}</Text>
          <Text>Date:   {dsr.date} - {dsr.date_to}</Text>
        </View>

        {/* Two Column Layout */}
        <View style={styles.row}>
          {/* LEFT SIDE */}
          <View style={{ flex: 1, marginRight: 5 }}>
            {/* OPR Section */}
            <View style={styles.box}>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>OPR-R#</Text>
                <Text style={styles.cell}>{`${dsr.retail_or_start}`}</Text>
                <Text style={styles.cell}>{`${dsr.retail_or_end}`}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>OPR-D#</Text>
                <Text style={styles.cell}>{`${dsr.dealer_or_start}`}</Text>
                <Text style={styles.cell}>{`${dsr.dealer_or_end}`}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>OPR-R</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.oprRetailSales)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>OPR-D</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.oprDealerSales)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>TOTAL</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.totalSales)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>ADD ONS</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.total_addons)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>LESS</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.total_expenses)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>ONLINE PAYMENT</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.total_online)}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>TOTAL</Text>
                <Text style={styles.cell_amt}>
                  ₱ {formatNum(dsr.totalSales - dsr.total_expenses - dsr.total_online)}
                </Text>
              </View>
            </View>

            {/* Denominations */}
            <Text style={{ fontWeight: "bold", marginBottom: 3 }}>
              {dsr.cashier_denom}
            </Text>
            <View style={styles.box}>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>DENOM</Text>
                <Text style={styles.cell}>PCS</Text>
                <Text style={styles.cell}>AMOUNT</Text>
              </View>

              {denominationsData.map((item) => (
                <View style={styles.tableRow} key={item.denom}>
                  <Text style={styles.cell_amt}>{item.denom}</Text>
                  <Text style={styles.cell_amt}>{item.pcs}</Text>
                  <Text style={styles.cell_amt}>
                    ₱ {formatNum(item.amount)}
                  </Text>
                </View>
              ))}

              <View style={styles.tableRow}>
                <Text style={styles.cell}>TOTAL CASH</Text>
                <Text style={styles.cell}></Text>
                <Text style={styles.cell_amt}>₱ {formatNum(totalCash)}</Text>
              </View>
            </View>
          </View>

          {/* RIGHT SIDE */}
          <View style={{ flex: 1, marginLeft: 5 }}>
            <Text style={{ fontWeight: "bold", marginBottom: 3 }}>
              DAILY LESS ON SALES
            </Text>
            <View style={styles.box}>
              <View style={styles.tableRow}>
                <Text style={styles.cell}>NO.</Text>
                <Text style={styles.cell}>VOUCHER #</Text>
                <Text style={styles.cell}>AMOUNT</Text>
                <Text style={[styles.cell, styles.wideCell]}>DETAILS</Text>
              </View>

              {dsr?.expense_list?.map((item, index) => (
                <View style={styles.tableRow}>
                  <Text style={styles.cell_amt}>{index + 1}</Text>
                  <Text style={styles.cell_amt}>{item.voucher}</Text>
                  <Text style={styles.cell_amt}>{formatNum(item.amount)}</Text>
                  <Text style={[styles.cell_amt, styles.wideCell]}>
                    {item.category}
                  </Text>
                </View>
              ))}
            </View>

            {/* ADD-ONS Section */}
            <Text style={{ fontWeight: "bold", marginBottom: 3 }}>ADD-ONS</Text>
            <View style={styles.box}>
              {dsr?.addons_list?.map((item, index) => (
                <View style={styles.tableRow}>
                  <Text style={styles.cell_amt}>{formatNum(item.amount)}</Text>
                  <Text style={[styles.cell_amt, styles.wideCell]}>
                    {item.category}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.footer}>
          <View style={styles.signBox}>
            <Text style={styles.underline}></Text>
            <Text>ENCODER</Text>
          </View>
          <View style={styles.signBox}>
            <Text style={styles.underline}></Text>
            <Text>CASHIER</Text>
          </View>
          <View style={styles.signBox}>
            <Text style={styles.underline}></Text>
            <Text>AUDITOR</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
